// @givmo/board — the words of "Import older records" one document at a time (CBG-UX11, D-CBG-39;
// design-284 `scratchpad/2026-10-24-import-without-batches-design/design.md` §2, §10, frames
// i1–i4, r1 and r2).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the design with its words as drafts (2026-10-24 14:28 PDT, "1)
// yes"); copy round CBG-UX-COPY3 approved them (D-CBG-45, 2026-10-29 08:53 PDT, rows I1–I10, I18 and
// I20), several reworded, with D-CBG-45's amendment of 08:57 PDT: a line naming the AI that acted
// says "AI assistant". One is his own sentence: the import page's quiet line (APPROVED, 14:28 PDT,
// "(i.e. Claude)" written "(for example, Claude)"), whose "Claude" is an example, not who acted.
// Lines already approved elsewhere and reused word for word (the page's title, the form's title, the
// file field's words, the closed session's) stay where they are approved and are not repeated here.
//
// ⚠ No screen names a batch (design-284 §2.5): the batch is an internal detail of a confirmation,
// never a person's word.

/** How one imported record came in, and who put it in the book (the record's `entered_by`): by
 *  hand, or proposed by an AI and confirmed by that person; `null` for a name not shown. */
export type CameIn =
  | { kind: 'by_hand'; by: string | null }
  | { kind: 'proposed'; by: string | null };

/** A person the directory gives no name for (the house's words). */
const UNNAMED_PERSON = 'someone whose name isn’t shown';

/** The same, set off before a date. */
const UNNAMED_PERSON_BEFORE_A_DATE = `${UNNAMED_PERSON},`;

export const importWords = {
  // ── The page (frame i1) ──────────────────────────────────────────────────────────────────────

  /** Approved (CBG-UX-COPY3's I1): the line under the title, for someone who can import. */
  pageLead:
    'Everything your board decided before it used Givmo belongs in your minute book too. Add each document here, one at a time.',
  /** The line under the title for a reader who can't import: the page's own first sentence. */
  pageLeadReader:
    'Everything your board decided before it used Givmo belongs in your minute book too.',
  /** APPROVED (Jonathan, 2026-10-24 14:28 PDT): the quiet line, for someone who can import. */
  connectorLine:
    'To process a whole folder of files at once, you can use the Givmo connector in your AI tool (for example, Claude) to process them all and you confirm them here.',

  // ── The form (frames i1, i2) ─────────────────────────────────────────────────────────────────

  /** Approved (CBG-UX-COPY3's I2): the form's lead, under its approved title "Record something
   *  from before": what goes in, that nothing is approved again here, and the way out of a mistake. */
  formLead:
    'It goes into your minute book as your board kept it, and isn’t approved again here. Once it’s in, it can’t be edited; a mistake is fixed with a correction.',
  /** Approved (CBG-UX-COPY3's I6): "Where this came from", optional on every record (Jonathan, 14:28
   *  PDT). */
  sourceNoteLabel: 'Where this came from (optional)',
  sourceNoteHelper: 'In your own words, like “the secretary’s binder, page 14”.',
  /** Approved (CBG-UX-COPY3's I6): the text, optional (the file holds it), and the line under it,
   *  new in that round, answering where the question is asked. */
  bodyLabel: 'What it says (optional)',
  bodyHelper: 'You can leave this empty when you add the file.',
  /** The pickers' "no" answers, shown rather than a blank "Select…" (design-284 §2.1). Approved
   *  (CBG-UX-COPY3's I4 and I7; review-295's M6): a fact about the minute book, and the two links'
   *  neutral empty choice, never "Not recorded", which reads as "never minuted". */
  replacesNothing: 'No — there’s no earlier version in your minute book',
  noneChosen: 'None chosen',

  // ── After "Record it" (frame i3) ─────────────────────────────────────────────────────────────

  /** Approved (CBG-UX-COPY3's I8): the line above the form after a record goes in; "Open it" is the
   *  link after it. It says the file went in too when one did (review-295's M5). Led by the title,
   *  it ends on its own words, so no full stop sits by the closing quote. */
  recorded: (title: string, withFile: boolean) =>
    withFile
      ? `“${title}” is in your minute book, with its file.`
      : `“${title}” is in your minute book.`,
  openIt: 'Open it',
  /** Approved (CBG-UX-COPY3's I9): a replay, answered with the record already there. */
  alreadyRecorded:
    'This one is already in your minute book, so nothing was added twice. If it’s a different document, say which is which in “Where this came from” and record it again.',

  // ── Added so far (frame i3) ──────────────────────────────────────────────────────────────────

  /** Approved (CBG-UX-COPY3's I10): the list's heading, in the page's own verb ("Add each document
   *  here"), its order (review-295's M4: by when each was added, not by its own date, which each row
   *  shows) and its foot. */
  importedHeading: 'Added so far',
  importedOrder: 'Most recently added first',
  seeThemAll: 'See them all in the minute book',
  /** Approved (CBG-UX-COPY3's I10, as Jonathan answered its AI form: "proposed by AI assistant,
   *  confirmed by {name}", the record page's verb): one row's quiet line, naming who added it; a
   *  record with no date leaves it out. */
  importedLine: (kind: string, date: string | null, cameIn: CameIn) =>
    [
      kind,
      date,
      cameIn.kind === 'by_hand'
        ? `added by ${cameIn.by ?? UNNAMED_PERSON}`
        : `proposed by AI assistant, confirmed by ${cameIn.by ?? UNNAMED_PERSON}`,
    ]
      .filter((part) => part !== null)
      .join(' · '),
  /** Approved (CBG-UX-COPY3's I10; not drawn): the list's read failed; "Try again" follows it. */
  importedFailed: 'We couldn’t load what’s been added.',

  // ── An imported record's own page (frames r1, r2) ────────────────────────────────────────────

  /** Approved (CBG-UX-COPY3's I18): how a record entered by hand came in; `null` for a name not
   *  shown. */
  enteredByHand: (name: string | null, date: string) =>
    `Entered by ${name ?? UNNAMED_PERSON_BEFORE_A_DATE} on ${date}.`,
  /** Approved (CBG-UX-COPY3's I20): an imported record with no text, which never takes text later. */
  textInTheFile: 'Its text is in the file.',
  noTextRecorded: 'No text was recorded with it.',
} as const;
